import { Injectable } from '@angular/core';
import { SimulationDao } from '@app/dao/simulation/simulation.dao';
import {
  GridkyProduct,
  GridKySearchCriteria,
} from '@app/dao/simulation/simulation.interface';
import { UserDao } from '@app/dao/user/user.dao';
import * as _ from 'lodash';
import { Observable, of, Subject, map, switchMap, tap } from 'rxjs';
import { GrikeyCriteria } from './ea-gridky-criteria/ea-gridky-criteria.component';
import {
  GridkyAreas,
  GridkyType,
} from './ea-gridky-header/ea-gridky-header.component';

export interface GridKySearchFilters {
  selectedArea?: GridkyAreas;
  selectedType?: GridkyType;
  lmnpType?: boolean;
}

@Injectable({ providedIn: 'root' })
export class EaGridkyService {
  constructor(
    private simulationDao: SimulationDao,
    private userDao: UserDao,
  ) {}

  product: GridkyProduct;
  criteria: GridKySearchCriteria;
  filters: GridKySearchFilters;
  userRp: any;

  productChanged: Subject<GridkyProduct> = new Subject();
  searchProductSubject: Subject<void> = new Subject<void>();

  find(
    criteria?: GridKySearchCriteria,
    filters?: GridKySearchFilters,
  ): Observable<any> {
    this.searchProductSubject.next();
    this.filters = { ...this.filters, ...filters };
    return this.userDao.getResidencePrincipal(true).pipe(
      map((address: any) =>
        address.googleResult ? (this.userRp = address) : null,
      ),
      switchMap(() => {
        this.applyFilters(criteria);

        if (
          this.filters &&
          this.filters.selectedArea != GridkyAreas.france &&
          !this.userRp
        ) {
          this.filters.selectedArea = GridkyAreas.around;
          this.resetProduct();
          return of(null);
        }
        return this.simulationDao
          .searchGrikey(this.criteria)
          .pipe(
            tap(this.resetProduct.bind(this)),
            map(this.setProduct.bind(this)),
          );
      }),
    );
  }

  resetProduct() {
    this.product = undefined;
    this.productChanged.next(undefined);
    return this.product;
  }

  setProduct(product: GridkyProduct) {
    this.product = product;
    this.productChanged.next(this.product);
    return this.product;
  }

  getProductType() {
    let value = '';
    switch (this.product.type) {
      case 'apartment':
        value = 'Appartement';
        break;
      case 'house':
        value = 'Maison';
        break;
    }
    return value;
  }

  getProductOrientation() {
    if (!this.product.orientations && !this.product.orientations[0]) return;
    let orientation = [];
    for (let index = 0; index < this.product.orientations[0].length; index++) {
      orientation.push(this.product.orientations[0][index]);
    }

    return orientation
      .map(o => {
        let value = '';
        switch (o) {
          case 'S':
            value = 'Sud';
            break;
          case 'W':
            value = 'Ouest';
            break;
          case 'E':
            value = 'Est';
            break;
          case 'N':
            value = 'Nord';
            break;
        }
        return value;
      })
      .join('-');
  }

  getCriterias(): GrikeyCriteria[] {
    if (!this.product) return [];
    return _([
      this.product.hasCellar
        ? {
            label: 'Sous-sol',
            value: `${this.product.cellarSurface} m²`,
            icon: 'sp-balcony',
          }
        : undefined,
      this.product.hasGarden
        ? {
            label: 'Jardin',
            value: `${this.product.gardenSurface} m²`,
            icon: 'sp-garden',
          }
        : undefined,
      this.product.hasLoggia
        ? {
            label: 'Balcon',
            value: `${this.product.loggiaSurface} m²`,
            icon: 'sp-balcony',
          }
        : undefined,
      this.product.hasParking
        ? {
            label: 'Parking',
            value: `${this.product.weightedSurface} m²`,
            icon: 'sp-balcony',
          }
        : undefined,
      this.product.hasTerrace
        ? {
            label: 'Terrasse',
            value: `${this.product.terraceSurface} m²`,
            icon: 'sp-terrace',
          }
        : undefined,
      // this.product.hasOption ? { label: 'Option', value: `${this.product.otherSurface} m²` } : undefined,
    ])
      .filter(criteria => !_.isNil(criteria))
      .value();
  }

  get urlDocuments() {
    return this.product.documents;
  }
  applyFilters(criteria: GridKySearchCriteria) {
    if (this.filters && this.filters.selectedArea == GridkyAreas.france)
      this.adaptCriteriaToFranceArea(criteria);
    else if (this.userRp) this.adaptCriteriaToAroundArea(criteria);

    if (this.filters) {
      this.adaptCriteriaToType();
    }
  }

  adaptCriteriaToFranceArea(
    criteria: GridKySearchCriteria,
  ): GridKySearchCriteria {
    this.filters.selectedArea = GridkyAreas.france;
    return (this.criteria = {
      ...this.criteria,
      ...criteria,
      distanceSearchLatitude: undefined,
      distanceSearchLongitude: undefined,
      distanceSearchRadiusKm: undefined,
    });
  }

  adaptCriteriaToAroundArea(
    criteria: GridKySearchCriteria,
  ): GridKySearchCriteria {
    this.filters.selectedArea = GridkyAreas.around;
    return (this.criteria = {
      ...this.criteria,
      ...criteria,
      distanceSearchLatitude: this.userRp.googleResult.geometry.location.lat(),
      distanceSearchLongitude: this.userRp.googleResult.geometry.location.lng(),
      distanceSearchRadiusKm: 50,
    });
  }

  adaptCriteriaToType(): GridKySearchCriteria {
    let criteria = {
      project: null,
      lmnpTypes: null,
    };

    if (this.filters.lmnpType)
      criteria['lmnpTypes'] = [this.filters.selectedType];
    else criteria['project'] = this.filters.selectedType;

    return (this.criteria = {
      ...this.criteria,
      ...criteria,
    });
  }

  changeSelectedArea(area: GridkyAreas) {
    if (this.filters.selectedArea == area) return;
    this.filters.selectedArea = area;
    const sub = this.find().subscribe(() => sub.unsubscribe());
  }

  changeSelectedType(type: GridkyType, lmnpType?: boolean) {
    if (this.filters.selectedType == type) return;
    this.filters.selectedType = type;
    this.filters.lmnpType = lmnpType;
    const sub = this.find().subscribe(() => sub.unsubscribe());
  }
}
